import { FlexRow } from '@/components/common/Flex';
import { Text } from '@/components/common/Typography';
import { useMutation } from '@apollo/client';
import { Box, BoxProps } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    AnyFinancialEntity,
    ExternalFinancialEntity,
    getDefaultName,
} from '@common/financialEntities';
import {
    SetFinancialEntityPermissionDocument,
    ShareStatus,
    UpdateFinancialEntityTypeDocument,
} from '@common/graphql/generated';
import {
    configByFECombinedType,
    FinancialEntityCombinedType,
    getFECombinedType,
    getFECombinedTypeOptions,
} from '@common/react/financialEntities';
import { useState } from 'react';
import ShareStatusActionMenu from '../common/ShareStatusActionMenu';
import AccountLockup from './AccountLockup';
import FinancialEntityTypeSelectMenuButton from './FinancialEntityTypeSelectMenuButton';

export interface AddedAccountsProps extends BoxProps {
    activePartnershipId?: string | null;
    accounts: ExternalFinancialEntity[];
}

const AddedAccounts = ({
    activePartnershipId,
    accounts,
    ...props
}: AddedAccountsProps) => {
    return (
        <Box {...props}>
            {accounts.map((account) => {
                return (
                    <FlexRow
                        key={account.id}
                        py={4}
                        gap={4}
                        w="full"
                        justifyContent="space-between"
                        alignItems="center"
                        _notFirst={{
                            borderTop: '1px solid',
                            borderColor: 'gray.200',
                        }}
                    >
                        <AccountLockup
                            account={account}
                            additionalSubtitleContent={
                                <AddedAccountType account={account} />
                            }
                        />
                        {activePartnershipId && (
                            // TODO: Consider switching to `ShareStatusToggle`
                            // so we can remove `ShareStatusActionMenu`. This is
                            // the last place where it is used.
                            <ShareStatusActionMenuContainer
                                account={account}
                                activePartnershipId={activePartnershipId}
                            />
                        )}
                    </FlexRow>
                );
            })}
        </Box>
    );
};

const AddedAccountType = ({
    account,
}: {
    account: ExternalFinancialEntity;
}) => {
    const [updateFinancialEntityType] = useMutation(
        UpdateFinancialEntityTypeDocument
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateCombinedType = (combinedType: FinancialEntityCombinedType) => {
        const feCombinedTypeConfig = configByFECombinedType[combinedType];
        assert(feCombinedTypeConfig);
        const { type, subtype } = feCombinedTypeConfig;
        setIsSubmitting(true);
        updateFinancialEntityType({
            variables: {
                financialEntityId: account.id,
                financialEntityType: type,
                financialEntitySubtype: subtype,
            },
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const feCombinedTypeOptions = getFECombinedTypeOptions(
        account.type,
        account.ownershipType
    );

    return feCombinedTypeOptions.length > 0 ? (
        <FinancialEntityTypeSelectMenuButton
            options={feCombinedTypeOptions}
            value={getFECombinedType(account.info)}
            onChange={(newFECombinedType) => {
                updateCombinedType(newFECombinedType);
            }}
            isLoading={isSubmitting}
        />
    ) : (
        <Text
            fontSize="sm"
            color="textMuted"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
        >
            {getDefaultName(account.type).replace(' Account', '')}
        </Text>
    );
};

const ShareStatusActionMenuContainer = ({
    account,
    activePartnershipId,
}: {
    account: AnyFinancialEntity;
    activePartnershipId: string;
}) => {
    const [setFinancialEntityPermission] = useMutation(
        SetFinancialEntityPermissionDocument
    );
    const [isSubmittingShareStatus, setIsSubmittingShareStatus] =
        useState(false);

    const updateShareStatus = (
        account: AnyFinancialEntity,
        shareStatus: ShareStatus
    ) => {
        setIsSubmittingShareStatus(true);
        setFinancialEntityPermission({
            variables: {
                financialEntityId: account.id,
                partnershipId: activePartnershipId,
                shareStatus,
            },
        }).finally(() => {
            setIsSubmittingShareStatus(false);
        });
    };

    return (
        <FlexRow>
            <ShareStatusActionMenu
                type="account"
                shareStatus={account.shareStatus}
                onChangeShareStatus={(shareStatus) =>
                    updateShareStatus(account, shareStatus)
                }
                menuButtonProps={{
                    minW: 32,
                    size: 'sm',
                    fontSize: 'sm',
                    width: 'auto',
                    isLoading: isSubmittingShareStatus,
                }}
            />
        </FlexRow>
    );
};

export default AddedAccounts;
